import { ActionIcon, Button, Flex, Text } from '@mantine/core';
import { IconMessageCircle } from '@tabler/icons-react';
import cn from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { matchPath, Outlet, type NavigateFunction, useLocation, useNavigate, useParams } from 'react-router-dom';

import ButtonBack from '@/components/ui/ButtonBack';
import MobileBottomNav from '@/components/ui/MobileBottomNav';
import Tabs from '@/components/ui/Tabs';
import {
  getCurrentDocumentSection,
  getFirstAvailableDocumentSectionRoute,
} from '@/configs/organizationSectionsRouting';
import { ROUTES } from '@/configs/routes';
import { getTabRoute } from '@/entities/MobileDocumentTabs';
import { TabKey } from '@/entities/MobileDocumentTabs/types';
import { useChatPanelState } from '@/hooks/useChatPanelState';
import { useOrganizationDocumentsBottomNav } from '@/hooks/useOrganizationDocumentsBottomNav';
import { useOrganizationSectionsStore } from '@/stores/local/shared/OrganizationSectionsStore';
import { useSidebarDocumentsStore } from '@/stores/local/sidebar/SidebarDocumentsStore';
import { navigateToChatWithSession } from '@/utils/chatNavigation';
import { useDevices } from '@/utils/devices';

import ChatPanel from './ChatPanel/ChatPanel';
import { ChatPanelContext, type ChatPanelOpenOptions } from './ChatPanel/ChatPanelContext';
import MobileHeader from './MobileHeader';
import { getDocumentTabs } from './config';
import { useActiveTab } from './useActiveTab';
import { findDocumentName } from './utils';

import s from './OrganizationDocumentsLayout.module.scss';

type SyncChatRouteParams = {
  id?: string;
  isChatMode: boolean;
  isChatEnabled: boolean;
  documentId?: string;
  enabledDocumentSections: Parameters<typeof getFirstAvailableDocumentSectionRoute>[0]['enabledSections'];
  selectedDocumentsCount: number;
  selectedDocumentIds?: readonly (string | number | null | undefined)[] | null;
  pathname: string;
  navigate: NavigateFunction;
};

type ResolveSectionFallbackRouteParams = {
  id?: string;
  documentId?: string;
  currentDocumentSection: ReturnType<typeof getCurrentDocumentSection>;
  isCurrentSectionEnabled: boolean;
  enabledDocumentSections: Parameters<typeof getFirstAvailableDocumentSectionRoute>[0]['enabledSections'];
};

const syncChatRoute = ({
  id,
  isChatMode,
  isChatEnabled,
  documentId,
  enabledDocumentSections,
  selectedDocumentsCount,
  selectedDocumentIds,
  pathname,
  navigate,
}: SyncChatRouteParams): void => {
  if (!id || !isChatMode) {
    return;
  }

  if (!isChatEnabled) {
    if (documentId) {
      const fallbackRoute = getFirstAvailableDocumentSectionRoute({
        enabledSections: enabledDocumentSections,
        organizationId: id,
        documentId: String(documentId),
      });

      navigate(fallbackRoute ?? ROUTES.organization.documents.create(id), { replace: true });

      return;
    }

    navigate(ROUTES.organization.documents.create(id), { replace: true });

    return;
  }

  if (selectedDocumentsCount > 1) {
    const chatPath = ROUTES.organization.documents.chat.create(id);

    if (pathname !== chatPath) {
      navigateToChatWithSession({ navigate, organizationId: id });
    }

    return;
  }

  if (!selectedDocumentIds || selectedDocumentIds.length !== 1) {
    return;
  }

  const [nextDocumentId] = selectedDocumentIds;

  if (!nextDocumentId) {
    return;
  }

  const nextPath = ROUTES.organization.documents.chat.create(String(id), String(nextDocumentId));

  if (nextPath && pathname !== nextPath) {
    navigateToChatWithSession({ navigate, organizationId: id, documentId: String(nextDocumentId) });
  }
};

const resolveSectionFallbackRoute = ({
  id,
  documentId,
  currentDocumentSection,
  isCurrentSectionEnabled,
  enabledDocumentSections,
}: ResolveSectionFallbackRouteParams): string | null => {
  if (!id || !documentId || !currentDocumentSection || isCurrentSectionEnabled) {
    return null;
  }

  return (
    getFirstAvailableDocumentSectionRoute({
      enabledSections: enabledDocumentSections,
      organizationId: id,
      documentId: String(documentId),
    }) ?? ROUTES.organization.documents.create(id)
  );
};

// eslint-disable-next-line sonarjs/cognitive-complexity -- Layout coordinates route guards and mobile/desktop UI states.
const OrganizationDocumentsLayout: React.FC = () => {
  const navigate = useNavigate();
  const { id, documentId: routeDocumentId } = useParams();
  const { pathname } = useLocation();
  const sidebarStore = useSidebarDocumentsStore();
  const sectionsStore = useOrganizationSectionsStore();
  const { selectionStore } = sidebarStore;
  const { isCompactViewport } = useDevices();
  const chatMatch = React.useMemo(() => matchPath(ROUTES.organization.documents.chat.path, pathname), [pathname]);
  const documentId = routeDocumentId ?? chatMatch?.params?.documentId;
  const enabledDocumentSections = sectionsStore.enabledDocumentSections;
  const isChatEnabled = sectionsStore.isSectionEnabled('chat');
  const documentPermissionsLoaded = documentId
    ? sidebarStore.permissionsStore.hasPermission(documentId, 'document')
    : false;
  const canEditProperties = documentId
    ? documentPermissionsLoaded && sidebarStore.permissionsStore.canEdit(documentId, 'document')
    : false;
  const documentTabs = getDocumentTabs(enabledDocumentSections, canEditProperties, {
    'document-preview': sectionsStore.getSectionName('document-preview') ?? undefined,
    'ai-summary': sectionsStore.getSectionName('ai-summary') ?? undefined,
    analyze: sectionsStore.getSectionName('analyze') ?? undefined,
  });

  const activeTab = useActiveTab(Number(id), documentId);
  const { chatPanel, openPanel: openPanelState, closePanel } = useChatPanelState();

  const openPanel = React.useCallback(
    (opts?: ChatPanelOpenOptions) => {
      if (!isChatEnabled) {
        return;
      }

      if (documentId) {
        selectionStore.selectDocumentsOnly([documentId]);
      } else {
        selectionStore.setSelectAll(true);
      }

      openPanelState(opts);
    },
    [isChatEnabled, documentId, selectionStore, openPanelState],
  );

  const chatPanelContextValue = React.useMemo(() => ({ openPanel, closePanel }), [openPanel, closePanel]);

  const handleOpenChat = React.useCallback(() => {
    openPanel();
  }, [openPanel]);

  const selectedDocumentIds = selectionStore.selectedDocumentIds;
  const selectedDocumentsCount = selectionStore.selectedDocumentsCount;

  const isChatMode = React.useMemo(() => {
    return Boolean(chatMatch);
  }, [chatMatch]);

  const isDocumentsRoot = React.useMemo(() => {
    return Boolean(matchPath({ path: ROUTES.organization.documents.path, end: true }, pathname));
  }, [pathname]);

  const isPreview = activeTab === TabKey.documentPreview;
  const shouldShowShadows = !isPreview && activeTab !== TabKey.analyze;
  const hasDocumentContext = Boolean(documentId);

  const showDocumentTabs =
    Boolean(documentId) && !(isChatMode && selectedDocumentsCount > 1) && documentTabs.length > 0;
  const documentName =
    documentId && sidebarStore.treeStore ? findDocumentName(sidebarStore.treeStore, String(documentId)) : null;
  const singleFolderId = selectionStore.singleFolderToServerFormat;
  const folderName =
    singleFolderId && sidebarStore.treeStore ? findDocumentName(sidebarStore.treeStore, String(singleFolderId)) : null;
  const contextTitle = documentName ?? folderName;

  React.useEffect(() => {
    if (!documentId) {
      return;
    }

    void sidebarStore.permissionsStore.fetchPermissions([{ id: documentId, type: 'document' }]);
  }, [documentId, sidebarStore.permissionsStore]);

  React.useEffect(() => {
    syncChatRoute({
      id,
      isChatMode,
      isChatEnabled,
      documentId,
      enabledDocumentSections,
      selectedDocumentsCount,
      selectedDocumentIds,
      pathname,
      navigate,
    });
  }, [
    selectedDocumentsCount,
    selectedDocumentIds,
    id,
    navigate,
    pathname,
    isChatMode,
    isChatEnabled,
    documentId,
    enabledDocumentSections,
  ]);

  React.useEffect(() => {
    if (!id || !documentId || !documentPermissionsLoaded || canEditProperties || activeTab !== TabKey.properties) {
      return;
    }

    const fallbackRoute =
      getFirstAvailableDocumentSectionRoute({
        enabledSections: enabledDocumentSections,
        organizationId: id,
        documentId: String(documentId),
      }) ?? ROUTES.organization.documents.create(id);

    navigate(fallbackRoute, { replace: true });
  }, [activeTab, canEditProperties, documentId, documentPermissionsLoaded, enabledDocumentSections, id, navigate]);

  const currentDocumentSection = React.useMemo(() => getCurrentDocumentSection(pathname), [pathname]);
  const isCurrentSectionEnabled = currentDocumentSection
    ? sectionsStore.isSectionEnabled(currentDocumentSection)
    : false;

  React.useEffect(() => {
    const fallbackRoute = resolveSectionFallbackRoute({
      id,
      documentId,
      currentDocumentSection,
      isCurrentSectionEnabled,
      enabledDocumentSections,
    });

    if (fallbackRoute) {
      navigate(fallbackRoute, { replace: true });
    }
  }, [currentDocumentSection, documentId, enabledDocumentSections, id, isCurrentSectionEnabled, navigate]);

  const handleTabChange = React.useCallback(
    (key: string) => {
      if (!documentId) {
        return;
      }

      navigate(getTabRoute(key as TabKey, Number(id), documentId as string));
    },
    [navigate, id, documentId],
  );

  const backToDocumentsPath = React.useMemo(() => {
    if (!id) {
      return null;
    }

    return ROUTES.organization.documents.create(String(id));
  }, [id]);

  const handleCloseMobile = React.useCallback(() => {
    if (!backToDocumentsPath) {
      return;
    }

    navigate(backToDocumentsPath);
  }, [backToDocumentsPath, navigate]);

  const items = React.useMemo(() => {
    const disableBySelection = Boolean(documentId) && isChatMode && selectedDocumentsCount > 1;

    return documentTabs.map((tab) => {
      const disabled = !documentId || disableBySelection || tab.disabled;
      const disabledTooltip = 'Выберите один документ, чтобы открыть раздел';

      return {
        ...tab,
        disabled,
        disabledTooltip,
      };
    });
  }, [documentId, documentTabs, isChatMode, selectedDocumentsCount]);

  const {
    items: mobileItems,
    activeKey: mobileActiveKey,
    onChange: handleMobileTabChange,
  } = useOrganizationDocumentsBottomNav({
    documentId: documentId ?? undefined,
    activeTab,
    isChatMode: isChatMode || chatPanel.opened,
    canEditProperties,
    onTabChange: handleTabChange,
  });

  const isMobileTab = React.useMemo(
    () => (isChatMode ? isChatEnabled : Boolean(activeTab) && mobileItems.some((tab) => tab.key === activeTab)),
    [activeTab, isChatEnabled, isChatMode, mobileItems],
  );

  const showMobileHeader =
    isCompactViewport && isMobileTab && (hasDocumentContext || (isChatMode && Boolean(folderName)));

  return (
    <ChatPanelContext.Provider value={chatPanelContextValue}>
      <div className={cn(s.root, isChatMode && s.root_chat, !showMobileHeader && s.root_no_mobile_header)}>
        {!isCompactViewport && !isDocumentsRoot && (
          <div className={s.root__header}>
            <Flex gap="sm" align="center" wrap="wrap">
              {isChatMode && backToDocumentsPath && (
                <ButtonBack
                  className={s.root__back}
                  title={
                    <Text size="sm" fw={500}>
                      Назад к документам
                    </Text>
                  }
                  to={backToDocumentsPath}
                />
              )}
              {!isCompactViewport && isChatEnabled && (
                <Button
                  variant="gradient"
                  gradient={{ from: '#56AFFC', to: '#465CFF', deg: 107.33 }}
                  size="sm"
                  onClick={handleOpenChat}
                >
                  Обсудить с ИИ
                </Button>
              )}
              {showDocumentTabs && !isCompactViewport && (
                <Tabs items={items} activeKey={activeTab} onChange={handleTabChange} />
              )}
            </Flex>
          </div>
        )}
        {showMobileHeader && (
          <div className={s.root__header}>
            <MobileHeader
              showDocumentTitle={Boolean(contextTitle)}
              documentName={contextTitle}
              contextType={documentName ? 'document' : 'folder'}
              onClose={handleCloseMobile}
              onChatClick={isChatEnabled && !isChatMode && !chatPanel.opened ? handleOpenChat : undefined}
            />
          </div>
        )}
        <div
          className={cn(
            s.root__content,
            isChatMode && s.root__content_chat,
            shouldShowShadows && s.root__content_with_shadows,
            isPreview && s.root__content_preview,
          )}
        >
          <Outlet />
        </div>
        {isCompactViewport && showDocumentTabs && (
          <MobileBottomNav
            className={s['root__bottom-nav']}
            items={mobileItems}
            activeKey={mobileActiveKey}
            onChange={handleMobileTabChange}
          />
        )}
        {isCompactViewport && isChatEnabled && !showDocumentTabs && !isChatMode && !chatPanel.opened && (
          <ActionIcon
            className={s.root__fab}
            size="xl"
            radius="xl"
            variant="filled"
            onClick={handleOpenChat}
            aria-label="Обсудить с ИИ"
          >
            <IconMessageCircle size={22} />
          </ActionIcon>
        )}
        <ChatPanel
          opened={chatPanel.opened}
          onClose={closePanel}
          organizationId={Number(id)}
          documentId={documentId}
          seedText={chatPanel.options?.seedText}
          analysisResultId={chatPanel.options?.analysisResultId}
        />
      </div>
    </ChatPanelContext.Provider>
  );
};

export default observer(OrganizationDocumentsLayout);
